import { ThunkAction } from "redux-thunk";
import { AnyAction } from "redux";
import { action } from "typesafe-actions";
import { IStoreState } from "../initialStoreState";
import { api } from "../../api/api";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import {
  IContactEnrollCronLog,
  IContactListRow,
  IContactListUpsert,
  IContactRow,
  IContactUpsert,
  IContactUploadResult,
  ContactStatus,
  ContactListStatus,
} from "./sequenceContactLists.types";

/* ================================================================== *
 * CONTACT LISTS — table
 * ================================================================== */
export const FETCH_CONTACT_LISTS_PROGRESS = "FETCH_CONTACT_LISTS_PROGRESS";
export const FETCH_CONTACT_LISTS_SUCCESS = "FETCH_CONTACT_LISTS_SUCCESS";
export const FETCH_CONTACT_LISTS_FAILED = "FETCH_CONTACT_LISTS_FAILED";

export const fetchContactListsProgress = () =>
  action(FETCH_CONTACT_LISTS_PROGRESS);
export const fetchContactListsSuccess = (
  data: IContactListRow[],
  totalRecords: number
) => action(FETCH_CONTACT_LISTS_SUCCESS, { data, totalRecords });
export const fetchContactListsFailed = (forbidden?: boolean) =>
  action(FETCH_CONTACT_LISTS_FAILED, { forbidden });

export interface IFetchContactListsParams {
  pageNo: number;
  itemPerPage: number;
  status?: string;
  column?: string;
  value?: string;
}

export const fetchContactListsAsync =
  (
    params: IFetchContactListsParams,
    onForbidden?: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchContactListsProgress());
      let url = `/sequence/get-sequence-contact-lists?pageNo=${params.pageNo}&itemPerPage=${params.itemPerPage}`;
      if (params.status && params.status !== "-1")
        url += `&status=${params.status}`;
      if (params.column && params.value) {
        url += `&columns=${params.column}&value=${encodeURIComponent(
          params.value
        )}`;
      }
      const res = await api.get(url);
      const data: IContactListRow[] = res.data.data;
      const totalRecords = res.data.totalRecords;
      dispatch(fetchContactListsSuccess(data, totalRecords));
    } catch (err: any) {
      // The server returns 403 "No module access for the role!" when the role
      // cannot see Contact Lists. The caller hides the menu/tab in that case
      // (UI PRD §1).
      const forbidden = err.response?.status === 403;
      dispatch(fetchContactListsFailed(forbidden));
      if (forbidden) {
        onForbidden?.();
        return;
      }
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to load contact lists",
          displayAs: "snackbar",
        })
      );
    }
  };

/**
 * Lightweight fetch of ACTIVE lists for the sequence-builder picker
 * (UI PRD §4). Does not touch the slice state.
 */
export const fetchActiveContactListsAsync =
  (
    onCallback: (lists: IContactListRow[]) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      const res = await api.get(
        `/sequence/get-sequence-contact-lists?status=ACTIVE&pageNo=1&itemPerPage=100`
      );
      onCallback(res.data.data || []);
    } catch (err: any) {
      onCallback([]);
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to load contact lists",
          displayAs: "snackbar",
        })
      );
    }
  };

/* ================================================================== *
 * SINGLE CONTACT LIST (detail header)
 * ================================================================== */
export const FETCH_CONTACT_LIST_PROGRESS = "FETCH_CONTACT_LIST_PROGRESS";
export const FETCH_CONTACT_LIST_SUCCESS = "FETCH_CONTACT_LIST_SUCCESS";
export const FETCH_CONTACT_LIST_FAILED = "FETCH_CONTACT_LIST_FAILED";

export const fetchContactListProgress = () =>
  action(FETCH_CONTACT_LIST_PROGRESS);
export const fetchContactListSuccess = (data: IContactListRow | null) =>
  action(FETCH_CONTACT_LIST_SUCCESS, { data });
export const fetchContactListFailed = () =>
  action(FETCH_CONTACT_LIST_FAILED);

/**
 * There is no dedicated get-one endpoint, so resolve a single list from the
 * list endpoint by matching on list_code. Cheap enough and keeps the header
 * counts in sync after edits/uploads.
 */
export const fetchContactListByCodeAsync =
  (listCode: number | string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchContactListProgress());
      const res = await api.get(
        `/sequence/get-sequence-contact-lists?pageNo=1&itemPerPage=100`
      );
      const all: IContactListRow[] = res.data.data || [];
      const match =
        all.find((l) => String(l.list_code) === String(listCode)) || null;
      dispatch(fetchContactListSuccess(match));
    } catch (err: any) {
      dispatch(fetchContactListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to load the list",
          displayAs: "snackbar",
        })
      );
    }
  };

/* ================================================================== *
 * UPSERT CONTACT LIST (create / edit / deactivate)
 * ================================================================== */
export const upsertContactListAsync =
  (
    payload: IContactListUpsert,
    onCallback: (
      isSuccess: boolean,
      saved?: IContactListRow,
      errorMessage?: string
    ) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post(
        "/sequence/upsert-sequence-contact-list",
        payload
      );
      const saved: IContactListRow = res.data.data;
      dispatch(
        showMessage({
          type: "success",
          message: "Contact list saved",
          displayAs: "snackbar",
        })
      );
      onCallback(true, saved);
    } catch (err: any) {
      const message =
        err.response?.data?.message || "Failed to save the contact list";
      onCallback(false, undefined, message);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

/** Deactivate / activate a list (UI PRD §1 row menu). */
export const updateContactListStatusAsync =
  (
    listCode: number,
    status: ContactListStatus,
    onCallback: (isSuccess: boolean, errorMessage?: string) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      await api.post("/sequence/upsert-sequence-contact-list", {
        list_code: listCode,
        status,
      });
      dispatch(
        showMessage({
          type: "success",
          message: status === "ACTIVE" ? "List activated" : "List deactivated",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      const message =
        err.response?.data?.message || "Failed to update the list";
      onCallback(false, message);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

/* ================================================================== *
 * CONTACTS — table inside a list
 * ================================================================== */
export const FETCH_CONTACTS_PROGRESS = "FETCH_CONTACTS_PROGRESS";
export const FETCH_CONTACTS_SUCCESS = "FETCH_CONTACTS_SUCCESS";
export const FETCH_CONTACTS_FAILED = "FETCH_CONTACTS_FAILED";

export const fetchContactsProgress = () => action(FETCH_CONTACTS_PROGRESS);
export const fetchContactsSuccess = (
  data: IContactRow[],
  totalRecords: number
) => action(FETCH_CONTACTS_SUCCESS, { data, totalRecords });
export const fetchContactsFailed = () => action(FETCH_CONTACTS_FAILED);

export interface IFetchContactsParams {
  list_code: number | string;
  pageNo: number;
  itemPerPage: number;
  status?: string;
  event?: string;
  email?: string;
  column?: string;
  value?: string;
}

export const fetchContactsAsync =
  (
    params: IFetchContactsParams
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchContactsProgress());
      let url = `/sequence/get-sequence-contact?list_code=${params.list_code}&pageNo=${params.pageNo}&itemPerPage=${params.itemPerPage}`;
      if (params.status && params.status !== "-1")
        url += `&status=${params.status}`;
      if (params.event) url += `&event=${encodeURIComponent(params.event)}`;
      if (params.email) url += `&email=${encodeURIComponent(params.email)}`;
      if (params.column && params.value) {
        url += `&columns=${params.column}&value=${encodeURIComponent(
          params.value
        )}`;
      }
      const res = await api.get(url);
      dispatch(
        fetchContactsSuccess(res.data.data || [], res.data.totalRecords || 0)
      );
    } catch (err: any) {
      dispatch(fetchContactsFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to load contacts",
          displayAs: "snackbar",
        })
      );
    }
  };

/**
 * Resolve contacts by their contact_list_code(s). Used by the Enrolments tab
 * to show contact name + email until the backend returns them with the
 * enrolment rows (UI PRD §6). Results are returned via callback for caching.
 */
export const fetchContactsByCodesAsync =
  (
    listCode: number | string,
    onCallback: (contacts: IContactRow[]) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      const res = await api.get(
        `/sequence/get-sequence-contact?list_code=${listCode}&pageNo=1&itemPerPage=500`
      );
      onCallback(res.data.data || []);
    } catch {
      onCallback([]);
    }
  };

/* ================================================================== *
 * UPSERT CONTACT (add / edit / status change)
 * ================================================================== */
export const upsertContactAsync =
  (
    payload: IContactUpsert,
    onCallback: (isSuccess: boolean, errorMessage?: string) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      await api.post("/sequence/upsert-sequence-contact", payload);
      dispatch(
        showMessage({
          type: "success",
          message: "Contact saved",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      const message =
        err.response?.data?.message || "Failed to save the contact";
      onCallback(false, message);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

/** Change a single contact's status (unsubscribe / deactivate / reactivate). */
export const updateContactStatusAsync =
  (
    contactListCode: number,
    status: ContactStatus,
    onCallback: (isSuccess: boolean, errorMessage?: string) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      await api.post("/sequence/upsert-sequence-contact", {
        contact_list_code: contactListCode,
        status,
      });
      dispatch(
        showMessage({
          type: "success",
          message: "Contact updated",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      const message =
        err.response?.data?.message || "Failed to update the contact";
      onCallback(false, message);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

/* ================================================================== *
 * UPLOAD CONTACTS (CSV / XLSX)
 * ================================================================== */
export const uploadContactsAsync =
  (
    listCode: number,
    file: File,
    onCallback: (
      isSuccess: boolean,
      result?: IContactUploadResult,
      errorMessage?: string
    ) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async () => {
    try {
      const formData = new FormData();
      formData.append("list_code", String(listCode));
      formData.append("file", file);
      // A 50k-row file can take a while; allow up to 2 minutes (UI PRD §3).
      const res = await api.post(
        "/sequence/upload-sequence-contacts",
        formData,
        { timeout: 120000 }
      );
      onCallback(true, res.data.data as IContactUploadResult);
    } catch (err: any) {
      const message =
        err.response?.data?.message || "Failed to upload contacts";
      onCallback(false, undefined, message);
    }
  };

/* ================================================================== *
 * ENROLMENT CRON LOG (activation feedback / view progress, §5)
 * ================================================================== */
export const fetchContactEnrollCronLogAsync =
  (
    sequenceCode: string,
    onCallback: (log: IContactEnrollCronLog | null) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async () => {
    try {
      const res = await api.get(
        `/sequence/get-cron-log?job_type=CONTACT_LIST_ENROLL&sequence_code=${sequenceCode}`
      );
      const rows: IContactEnrollCronLog[] = res.data.data || [];
      // Newest first; the endpoint returns a list, take the latest run.
      onCallback(rows.length > 0 ? rows[0] : null);
    } catch {
      onCallback(null);
    }
  };

/* ================================================================== *
 * MANUAL ENROL (contact-list sequences, §6)
 * ================================================================== */
export interface IContactEnrolResult {
  module_code: string;
  created: boolean;
  enrollment_code?: string;
  reason?: string;
}

export const enrollContactsAsync =
  (
    sequenceCode: string,
    moduleCodes: (string | number)[],
    onCallback: (isSuccess: boolean, results?: IContactEnrolResult[]) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      // Chunk to 500 per request to mirror the sequence enrol flow.
      const CHUNK = 500;
      const all: IContactEnrolResult[] = [];
      for (let i = 0; i < moduleCodes.length; i += CHUNK) {
        const chunk = moduleCodes.slice(i, i + CHUNK).map(String);
        const res = await api.post("/sequence/enroll", {
          sequence_code: sequenceCode,
          module_name: "CONTACT_LIST",
          module_codes: chunk,
        });
        all.push(...(res.data.data || []));
      }
      onCallback(true, all);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to enrol contacts",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

/* ================================================================== *
 * CLEAR
 * ================================================================== */
export const CLEAR_CONTACT_LIST_DETAIL = "CLEAR_CONTACT_LIST_DETAIL";
export const CLEAR_SEQUENCE_CONTACT_LISTS_STATE =
  "CLEAR_SEQUENCE_CONTACT_LISTS_STATE";

export const clearContactListDetail = () =>
  action(CLEAR_CONTACT_LIST_DETAIL);
export const clearSequenceContactListsState = () =>
  action(CLEAR_SEQUENCE_CONTACT_LISTS_STATE);
